/* eslint-disable import/prefer-default-export */
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { find } from 'lodash';

import CustomerAddressModel from "../../../../components/CustomerAddresses/CustomeraddressModel";
import { uuid } from '../../../../util/utils';

export const useCustomerAddresses = ({
  customer,
  getValues,
  setValue,
  control,
  useWatch,
  reset,
  tCommon
}) => {
  const [openedCustomerAddress, setOpenedCustomerAddress] = useState(false);
  const [customerAddressesOptions, setCustomerAddressesOptions] = useState([]);
  const [dropdownKey, setDropdownKey] = useState(0);
  const addressesFetchedRef = useRef(false);

  const customerWebId = customer?.web_id_in_mercately || customer?.web_id || '';

  const addNewAddressOption = useMemo(
    () => ({
      id: 'new_address',
      value: 'new_address',
      label: `+ ${tCommon('labels.addNewAddress')}`
    }),
    [tCommon]
  );

  const orderAddressValue = useWatch({ name: 'orderAddress', control });

  const formatAddress = (fullAddress) => {
    const { address, city, state } = fullAddress;
    const parts = [address, city, state].filter(Boolean);
    return parts.join(', ');
  };

  const buildCustomerAddressesOptions = useCallback(
    (addresses) => {
      const newOptions = addresses
        .filter(({ address, city, state }) => address || city || state)
        .map((address) => ({
          ...address,
          id: String(address.id),
          value: address.id,
          label: formatAddress(address)
        }));

      newOptions.push(addNewAddressOption);
      setCustomerAddressesOptions(newOptions);
    },
    [addNewAddressOption]
  );

  const addAddressToOrder = useCallback((add) => {
    const {
      address, description, city, state, zip_code, country_id
    } = add;

    reset({
      ...getValues(),
      address: address || '',
      description: description || '',
      city: city || '',
      state: state || '',
      zip_code: zip_code || '',
      country_id: country_id || ''
    });
  }, [reset, getValues]);

  // Cargar direcciones del cliente solo la primera vez
  useEffect(() => {
    const web_id = customerWebId;

    if (!web_id) {
      setCustomerAddressesOptions([addNewAddressOption]);
      return;
    }

    // Solo cargar si no se ha cargado antes
    if (addressesFetchedRef.current) {
      return;
    }

    addressesFetchedRef.current = true;

    CustomerAddressModel.get(web_id)
      .then((res) => {
        buildCustomerAddressesOptions(res.addresses);
      })
      .catch((error) => {
        console.error('Error loading addresses:', error);
        setCustomerAddressesOptions([addNewAddressOption]);
      });
  }, [customerWebId, addNewAddressOption, buildCustomerAddressesOptions]);

  // Manejar cambios en orderAddress
  useEffect(() => {
    if (!orderAddressValue) return;

    if (orderAddressValue === 'new_address') {
      setOpenedCustomerAddress(true);
      return;
    }

    const add = find(customerAddressesOptions, { id: orderAddressValue });

    if (add) {
      addAddressToOrder(add);
    }
  }, [orderAddressValue, customerAddressesOptions, addAddressToOrder]);

  // Seleccionar dirección actual cuando editas una orden
  useEffect(() => {
    if (customerAddressesOptions.length <= 1) return; // Solo si hay direcciones cargadas

    const currentAddress = getValues('address');
    const currentCity = getValues('city');
    const currentState = getValues('state');

    // Si hay dirección en el formulario y no hay una seleccionada en el dropdown
    if (currentAddress) {
      // Buscar la dirección que coincida exactamente
      const matchingAddress = customerAddressesOptions.find(
        // Comparar trimmed para evitar espacios en blanco
        (option) => String(option.address).trim() === String(currentAddress).trim()
          && String(option.city).trim() === String(currentCity).trim()
          && String(option.state).trim() === String(currentState).trim()
      );

      if (matchingAddress && !orderAddressValue) {
        // Seleccionar la dirección encontrada
        setValue('orderAddress', String(matchingAddress.id));
        setDropdownKey((k) => k + 1);
      }
    }
  }, [customerAddressesOptions, getValues, orderAddressValue, setValue]);

  // Callback para actualizar direcciones después de agregar una nueva
  const updateAddressOptions = useCallback(
    (address) => {
      if (!address) return;

      const web_id = customerWebId;
      const id = address.id || uuid();

      if (web_id) {
        // Cargar direcciones del servidor
        CustomerAddressModel.get(web_id)
          .then((res) => {
            buildCustomerAddressesOptions(res.addresses);
            // Seleccionar la dirección guardada
            setValue('orderAddress', String(id));
            setDropdownKey((k) => k + 1);
            setOpenedCustomerAddress(false);
          })
          .catch((error) => {
            console.error('Error loading addresses after update:', error);
          });
      } else {
        // Para clientes sin web_id, actualizar localmente
        const fullAddress = {
          ...address,
          id
        };
        buildCustomerAddressesOptions([fullAddress]);
        setValue('orderAddress', String(id));
        setDropdownKey((k) => k + 1);
        setOpenedCustomerAddress(false);
      }
    },
    [customerWebId, buildCustomerAddressesOptions, setValue]
  );

  const toggleModal = useCallback(
    () => {
      if (!openedCustomerAddress && orderAddressValue === 'new_address') {
        // Si se cierra el modal sin guardar, limpiar la selección
        setValue('orderAddress', '');
        setDropdownKey((k) => k + 1);
      }
      setOpenedCustomerAddress((prev) => !prev);
    },
    [openedCustomerAddress, orderAddressValue, setValue]
  );

  return {
    customerWebId,
    dropdownKey,
    openedCustomerAddress,
    customerAddressesOptions,
    orderAddressValue,
    updateAddressOptions,
    toggleModal
  };
};
